<template>
  <fieldset class="px-2 md:px-0">
    <legend class="hidden">
      {{ fieldset.legend }}
    </legend>

    <div v-for="field in fieldset.fields" :key="field.key">
      <content-section
        v-if="!field.hasOwnProperty('repeatable')"
        class="form-section mt-12"
        :has-tabs="hasTabs"
      >
        <template #title>
          <template v-if="field.name && field.label">
            <h2 v-html="field.name" />
            <h4 class="mb-2" v-html="field.label" />
          </template>
          <template v-else>
            <h2 v-html="field.label" />
          </template>
          <div
            class="italic text-sm text-grey-700"
            v-html="parseMarkdown($sanitizeHtml(field.instructions))"
          />
          <input-validation-details :fields="field.sub_fields" />
          <assessment-inline-grade
            v-if="isAssessment && field.markable"
            :question="field"
          />
        </template>

        <div class="-mx-6 flex flex-wrap -mt-4 -mb-8">
          <template v-for="(input, index) in field.sub_fields">
            <input-break
              v-if="input.type === 'break'"
              :key="field.key + '_break_' + index"
              :input="input"
            />
            <input-categories
              v-else-if="input.type === 'categories'"
              :key="field.key + index"
              :input="input"
              :fields="groupingFields(field.sub_fields, input.grouping)"
              :values="application[field.key]"
              :error="hasError(field, input)"
            />
            <component
              :is="`Input${componentLookup[input.type]}`"
              v-else
              :key="input.key"
              :review-mode="reviewMode"
              :input="input"
              :parent-field="field.key"
              :application-value="application[field.key][input.key]"
              :error="hasError(field, input)"
              @update:input="updateValue"
              @blur="$emit('application:blur')"
            />
          </template>
        </div>
        <template #suffix>
          <portal-target :name="field.key" slim />
        </template>
      </content-section>

      <div v-else-if="field.hasOwnProperty('repeatable') && field.type === 'consortium'">
        <collapse-transition group>
          <content-section
            v-for="(repeatable_field, index) in application[field.key]"
            :key="index"
            class="form-section mt-12"
            :has-tabs="hasTabs"
          >
            <template #title>
              <h2 v-html="$sanitizeHtml(field.label + ' ' + (index + 1))" />
              <button
                v-if="hasMin(field.key) && !reviewMode"
                class="mt-2 cursor-pointer"
                type="button"
                @click="removeItem(field.key, index)"
              >
                <app-badge icon="close-circle" type="negative">
                  Delete
                </app-badge>
              </button>
              <p
                class="italic text-sm text-grey-700"
                v-html="parseMarkdown($sanitizeHtml(field.instructions))"
              />
              <input-validation-details :fields="field.sub_fields" />
            </template>

            <div class="-mx-6 flex flex-wrap -mb-8">
              <component
                :is="`Input${componentLookup[input.type]}`"
                v-for="input in field.sub_fields.filter((sub_field) => !['break','categories'].inclues(sub_field.type))"
                :key="input.key"
                :review-mode="reviewMode"
                :input="input"
                :parent-field="field.key"
                :application-value-index="index"
                :application-value="application[field.key][index][input.key]"
                :error="hasError(field, input, index)"
                @update:input="updateValue"
                @blur="$emit('application:blur')"
              />
            </div>
            <template #suffix>
              <portal-target :name="field.key + '_' + index" slim />
            </template>
          </content-section>
        </collapse-transition>
        <div
          v-if="hasMax(field.key) && !reviewMode"
          class="border-dashed border-b-2 border-grey-300 flex justify-center mb-8"
        >
          <button type="button" class="-mb-3" @click="addPartner(field.key)">
            <app-badge type="secondary" icon="add-circle">
              Add
            </app-badge>
          </button>
        </div>
      </div>

      <content-section v-else class="form-section mt-12" :has-tabs="hasTabs">
        <template #title>
          <h2 v-html="field.label" />
          <p
            class="italic text-sm text-grey-700"
            v-html="parseMarkdown($sanitizeHtml(field.instructions))"
          />
          <input-validation-details :fields="field.sub_fields" />
        </template>

        <div class="-mb-8">
          <collapse-transition group>
            <div
              v-for="(repeatable_field, index) in application[field.key]"
              :key="index"
              class="-mx-6 flex flex-wrap"
            >
              <div
                class="w-full px-6 flex items-center"
              >
                <h3>{{ field.label }} {{ index + 1 }}</h3>
                <button
                  v-if="hasMin(field.key) && !reviewMode"
                  class="cursor-pointer ml-auto my-auto"
                  type="button"
                  @click="removeItem(field.key, index)"
                >
                  <app-badge icon="close-circle" type="negative">
                    Delete
                  </app-badge>
                </button>
              </div>
              <component
                :is="`Input${componentLookup[input.type]}`"
                v-for="input in field.sub_fields"
                :key="input.key"
                :review-mode="reviewMode"
                :input="input"
                :parent-field="field.key"
                :application-value-index="index"
                :application-value="application[field.key][index][input.key]"
                :error="hasError(field, input, index)"
                @update:input="updateValue"
                @blur="$emit('application:blur')"
              />
            </div>
          </collapse-transition>
          <div
            v-if="hasMax(field.key) && !reviewMode"
            :class="{
              'border-dashed border-b-2 border-grey-300 flex justify-center mb-8':
                application[field.key].length,
            }"
          >
            <button
              type="button"
              :class="{
                '-mb-3': application[field.key].length,
                'w-full h-48 border-dashed border-2 border-grey-300 flex justify-center items-center mb-8': !application[
                  field.key
                ].length,
              }"
              @click="addItem(field.key)"
            >
              <app-badge type="secondary" icon="add-circle">
                Add
              </app-badge>
            </button>
          </div>
        </div>
      </content-section>
    </div>
  </fieldset>
</template>

<script>
import { CollapseTransition } from "vue2-transitions";
import merge from "lodash/merge";
import { store } from "../store/application";

export default {
  name: "ApplicationSection",
  components: {
    CollapseTransition,
  },
  props: {
    hasTabs: {
      type: Boolean,
      default: false,
    },
    fieldset: {
      type: Object,
      required: true,
    },
    errors: {
      type: Object,
      required: false,
      default: () => {},
    },
    showErrors: {
      type: Boolean,
      default: false,
    },
    isAssessment: {
      type: Boolean,
      required: false,
      default() {
        return false;
      },
    },
  },
  data() {
    return {
      application: store.application,
      componentLookup: {
        break: "Break",
        text: "Default",
        url: "Default",
        company: "Company",
        multiline: "Multiline",
        number: "Default",
        date: "Date",
        email: "Default",
        tel: "Default",
        toggle: "Toggle",
        select: "Select",
        checkbox: "Multi",
        radio: "Multi",
        upload: "Upload",
        dropzone: "Dropzone",
        consortium: "Consortium",
        stream: "Stream",
      },
    };
  },
  computed: {
    reviewMode() {
      return store.reviewMode;
    },
  },
  methods: {
    /**
     *
     * @param subFields {Array}  the sub_fields array from the field
     * @param grouping  {Array}  the grouping array from the input
     *
     * @return {Array}          the filtered sub_fields that match the grouping keys
     */
    groupingFields(subFields, grouping) {
      return subFields.filter((field) => grouping.includes(field.key));
    },
    /**
     * handleUpdate: handle the update:section event data
     * @param 	{string} 				parent 	the parent key
     * @param 	{string} 				key 		the field key
     * @param 	{string|object} value 	the field's value
     * @param 	{int}		 				index 	if value is in an array, the index of the item
     * @return												updates the main application with the updated key:value
     */
    updateValue({ parent, key, value, index, save }) {
      if (this.reviewMode) return;
      if (index === null || index === undefined) {
        const item = {
          [parent]: { ...this.application[parent], [key]: value },
        };
        if (Array.isArray(this.application[parent][key])) {
          this.application = Object.assign(this.application, item);
        } else {
          this.application = merge(this.application, item);
        }
        if (save) return this.$emit("application:save");
        return;
      }
      this.application[parent][index][key] = value;
      if (save) return this.$emit("application:save");
      return;
    },

    addItem(parentKey) {
      if (this.reviewMode) return;
      const field = this.fieldset.fields.find((item) => item.key === parentKey);
      if (!field) return;

      const keys = field.sub_fields.reduce((obj, item) => {
        obj[item.key] = item.attributes.default;
        return obj;
      }, {});
      return this.application[parentKey].push(keys);
    },

    addPartner(parentKey) {
      if (this.reviewMode) return;
      const field = this.fieldset.fields.find((item) => item.key === parentKey);
      if (!field) return;

      let keys = field.sub_fields.reduce((obj, item) => {
        obj[item.key] = item.attributes.default;
        return obj;
      }, {});

      keys = {
        ...keys,
        financial_summary: {
          requested_grant: null,
          contribution: null,
          total_project_cost: null,
        },
      };

      return this.application[parentKey].push(keys);
    },

    removeItem(parentKey, index) {
      if (this.reviewMode) return;
      if (!this.application[parentKey]) return;
      if (!confirm("Are you sure you want to delete this item?")) return;
      return this.application[parentKey].splice(index, 1);
    },

    hasMin(parentKey) {
      const field = this.fieldset.fields.find((item) => item.key === parentKey);
      if (!field) return;
      return field.repeatable.min
        ? this.application[parentKey].length > field.repeatable.min
        : true;
    },

    hasMax(parentKey) {
      const field = this.fieldset.fields.find((item) => item.key === parentKey);
      if (!field) return;
      return field.repeatable.max
        ? this.application[parentKey].length < field.repeatable.max
        : true;
    },

    hasError(field, input, index = false) {
      if (!this.showErrors) return;
      const parent = field.key,
        pos = typeof index === "number" ? "." + index : "",
        value = "." + input.key;
      const key = parent + pos + value;
      if (input.type === "consortium") {
        return this.consortiumErrors(key);
      } else {
        return this.errors[parent + pos + value];
      }
    },
    consortiumErrors(key) {
      const errors = Object.keys(this.errors)
        .filter((k) => k.includes(key))
        .reduce((obj, k) => {
          obj[k.replace(key + ".", "")] = this.errors[k];
          return obj;
        }, {});
      return Object.keys(errors).length ? errors : undefined;
    },
  },
};
</script>
